import { useCallback, useEffect, useState } from 'react'
import { Cake, Heart, Plus, Pencil, Trash2, X, PartyPopper, Bell, BellOff } from 'lucide-react'
import type { BirthdayDto, BirthdayNotify, OccasionKind } from '@familynest/shared'
import { api } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import { Button, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'

const field = 'w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}
/** Datum ohne Jahr (für Einträge ohne bekanntes Geburtsjahr). */
function fmtDayMonth(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${d}.${m}.`
}
const NOTIFY_OPTIONS: Array<{ v: BirthdayNotify; label: string }> = [
  { v: 'all', label: 'Alle' },
  { v: 'adults', label: 'Nur Eltern' },
  { v: 'self', label: 'Nur ich' },
  { v: 'none', label: 'Aus' },
]
function notifyLabel(b: BirthdayDto): string {
  if (b.notify === 'none') return 'Aus'
  if (b.notify === 'adults') return 'Nur Eltern'
  if (b.notify === 'self') return `Nur ${b.notifyMemberName ?? 'ich'}`
  return 'Alle'
}

interface OccasionConfig {
  title: string
  add: string
  dialogNew: string
  dialogEdit: string
  empty: (adult: boolean) => string
  ageChip: (n: number) => string
  datePrefix: string
  round: string
  namePlaceholder: string
  eventNote: (name: string) => string
  removeConfirm: (name: string) => string
  Icon: typeof Cake
}
const CONFIG: Record<OccasionKind, OccasionConfig> = {
  birthday: {
    title: 'Geburtstage',
    add: 'Geburtstag',
    dialogNew: 'Neuer Geburtstag',
    dialogEdit: 'Geburtstag bearbeiten',
    empty: (a) => (a ? 'Noch keine Geburtstage. Tippe „Geburtstag", um einen anzulegen.' : 'Noch keine Geburtstage eingetragen.'),
    ageChip: (n) => `${n} Jahre`,
    datePrefix: '* ',
    round: 'runder Geburtstag',
    namePlaceholder: 'Name (z. B. Oma Erna)',
    eventNote: (name) => `Geburtstag von ${name}`,
    removeConfirm: (name) => `Geburtstag von „${name}" löschen? (Kalendertermin wird mit entfernt)`,
    Icon: Cake,
  },
  anniversary: {
    title: 'Jahrestage',
    add: 'Jahrestag',
    dialogNew: 'Neuer Jahrestag',
    dialogEdit: 'Jahrestag bearbeiten',
    empty: (a) => (a ? 'Noch keine Jahrestage. Tippe „Jahrestag", um einen anzulegen.' : 'Noch keine Jahrestage eingetragen.'),
    ageChip: (n) => `${n}. Jahrestag`,
    datePrefix: '',
    round: 'runder Jahrestag',
    namePlaceholder: 'Anlass (z. B. Hochzeitstag)',
    eventNote: (name) => `Jahrestag: ${name}`,
    removeConfirm: (name) => `Jahrestag „${name}" löschen? (Kalendertermin wird mit entfernt)`,
    Icon: Heart,
  },
}

/**
 * Anlass-Liste (ADR-036): Geburtstage bzw. Jahrestage — gleiche Struktur. Alle
 * sehen sie, nur Eltern pflegen. Pro Eintrag Name, Datum, „Alter"/Jahre + nächster
 * runder Anlass; jeder Eintrag erzeugt einen jährlichen Kalendertermin.
 */
export function OccasionsScreen({ kind }: { kind: OccasionKind }): React.JSX.Element {
  const cfg = CONFIG[kind]
  const dialogs = useDialogs()
  const { member } = useAuth()
  const isAdult = member?.role === 'adult'
  const [items, setItems] = useState<BirthdayDto[]>([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<{ editing: BirthdayDto | null } | null>(null)

  const load = useCallback(async () => {
    try {
      setItems(await api.listOccasions(kind))
    } finally {
      setLoading(false)
    }
  }, [kind])
  useEffect(() => {
    void load()
  }, [load])
  useLive('calendar', load)

  async function remove(b: BirthdayDto): Promise<void> {
    if (await dialogs.confirm({ title: 'Löschen', message: cfg.removeConfirm(b.name), confirmLabel: 'Löschen', danger: true })) {
      await api.deleteOccasion(kind, b.id)
      await load()
    }
  }

  const Icon = cfg.Icon
  return (
    <div className="flex min-h-full flex-col gap-3 p-3 md:p-4">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{cfg.title}</h1>
        {isAdult && (
          <Button className="ml-auto" onClick={() => setDialog({ editing: null })}>
            <Plus className="size-4" /> {cfg.add}
          </Button>
        )}
      </div>

      {loading ? null : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
          <Icon className="size-8" />
          {cfg.empty(!!isAdult)}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((b) => {
            const roundSoon = b.nextRoundDate && b.nextRoundDate === b.nextBirthday
            return (
              <li key={b.id} className="flex items-center gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
                <div className="flex size-11 shrink-0 flex-col items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <Icon className="size-5" style={{ color: 'var(--color-accent)' }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>{b.name}</span>
                    {b.currentAge != null && (
                      <span className="shrink-0 rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)' }}>{cfg.ageChip(b.currentAge)}</span>
                    )}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    {cfg.datePrefix}{b.hasYear ? fmtDate(b.birthdate) : fmtDayMonth(b.birthdate)}
                    {b.nextRoundAge != null && b.nextRoundDate && (
                      <span style={{ color: roundSoon ? 'var(--color-accent)' : undefined }}>
                        {' '}· {cfg.round}: {b.nextRoundAge}. am {fmtDate(b.nextRoundDate)}
                        {roundSoon ? ' 🎉' : ''}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                    {b.notify === 'none' ? <BellOff className="size-3" /> : <Bell className="size-3" />}
                    Benachrichtigung: {notifyLabel(b)}
                  </div>
                </div>
                {isAdult && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => setDialog({ editing: b })} aria-label="Bearbeiten" className="rounded p-1 hover:bg-black/5"><Pencil className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                    <button onClick={() => void remove(b)} aria-label="Löschen" className="rounded p-1 hover:bg-black/5"><Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {dialog && (
        <OccasionDialog
          kind={kind}
          cfg={cfg}
          editing={dialog.editing}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

function OccasionDialog({ kind, cfg, editing, onClose, onSaved }: { kind: OccasionKind; cfg: OccasionConfig; editing: BirthdayDto | null; onClose: () => void; onSaved: () => Promise<void> }): React.JSX.Element {
  const [name, setName] = useState(editing?.name ?? '')
  const [birthdate, setBirthdate] = useState(editing?.birthdate ?? '')
  const [notify, setNotify] = useState<BirthdayNotify>(editing?.notify ?? 'all')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(): Promise<void> {
    if (!name.trim() || !birthdate) {
      setError('Bitte Name und Datum angeben.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (editing) await api.updateOccasion(kind, editing.id, { name: name.trim(), birthdate, notify })
      else await api.createOccasion(kind, { name: name.trim(), birthdate, notify })
      await onSaved()
    } catch {
      setError('Speichern fehlgeschlagen. Bitte Eingaben prüfen.')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-sm overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
            <PartyPopper className="size-5" style={{ color: 'var(--color-accent)' }} /> {editing ? cfg.dialogEdit : cfg.dialogNew}
          </h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="flex flex-col gap-3">
          <input className={field} style={fieldStyle} placeholder={cfg.namePlaceholder} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Datum</span>
            <input type="date" className={field} style={fieldStyle} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Benachrichtigung am Tag</span>
            <select className={field} style={fieldStyle} value={notify} onChange={(e) => setNotify(e.target.value as BirthdayNotify)}>
              {NOTIFY_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </label>
          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Es wird automatisch ein jährlicher Kalendertermin „{cfg.eventNote(name.trim() || '…')}" angelegt.</p>
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !name.trim() || !birthdate}>{editing ? 'Speichern' : 'Anlegen'}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
